import { Box } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { AddressContactRow } from './AddressContactRow';
import { EditFormShell } from './EditFormShell';
import { StructureAddressContactLayoutProps } from './StructureAddressContactLayout.props';
import { RoleBox, StyledTypography } from './StructureAddressContactLayout.styles';
import { ADDRESS_CONTACT_TYPES, DEFAULT_ADDRESS_COUNTRY } from '../../../constants/structure';
import { isEmptyString } from '../../../utils/common';
import { AddRowButton } from '../../atoms/AddRowButton';
import { TypographyVariantProps } from '../../atoms/Typography';
import { Contact } from '../../molecules/Contact';
import { StructureAddress } from '../../organisms/StructureAddress';

/** A field on an address as the wire actually sends it, not as the type claims. */
type AddressPart = string | number | null | undefined;

export interface FormattableAddress {
  line1?: AddressPart;
  line2?: AddressPart;
  suburb?: AddressPart;
  city?: AddressPart;
  postCode?: AddressPart;
  stateName?: AddressPart;
  countryName?: AddressPart;
  contactNumber?: AddressPart;
}

/**
 * Joins the parts of an address that are present, without stranding punctuation.
 *
 * Every part is coerced rather than trusted: `AddressDataType` declares these
 * as strings, but postcodes and contact numbers arrive from the BFF as numbers,
 * and calling a string method on one took the whole Location tab down.
 */
export const formatAddress = (data?: FormattableAddress): string =>
  [
    data?.line1,
    data?.line2,
    data?.suburb,
    data?.city,
    data?.postCode,
    data?.stateName,
    data?.countryName,
    data?.contactNumber,
  ]
    .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
    .filter(Boolean)
    .join(', ');

export const StructureAddressContactLayout: React.FC<StructureAddressContactLayoutProps> = ({
  heading,
  isAddress,
  isOneAddress,
  contactType,
  contactFormData,
  addressFormData,
  editContactFormData,
  editAddressFormData,
  addressDataset,
  contactDataset,
  enableContactNumber,
  addressForm = [],
  contactForm = [],
  setAddressForm = () => [],
  setContactForm = () => [],
  onClickContactEdit,
  onClickContactDelete,
  onClickAddressEdit,
  onClickAddressDelete,
  onStructureAddressChange,
  handleAddressCreate,
  handleContactCreate,
  onContactChange,
  addFormEnabledByDefault,
  enableAddressContactFields,
  hideAddressType,
  hasContractAddressEditPermission = true,
  flush,
  hideHeading = false,
  unmatchedSuburb,
}) => {
  const [isEnabledAdd, setIsEnabledAdd] = useState<boolean>(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(true);

  const addressRequiredFields = ['line1', 'suburb', 'stateName', 'postCode', 'countryName'];

  // These four effects all mean "the named prop changed, push it outward" and
  // are deliberately keyed on that prop alone. Every consumer passes its
  // callbacks as fresh arrow functions, so listing them would fire the effect on
  // each render — and each of these effects calls a setter, which renders again.
  // That is a refetch loop, not a missing dependency.
  useEffect(() => {
    if (contactForm.length) {
      const contactList = contactForm.map((data) => data.value);
      onContactChange && onContactChange([...contactList]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactForm]);

  useEffect(() => {
    if (addressForm.length) {
      const addressList = addressForm.map((data) => data.value);
      onStructureAddressChange && onStructureAddressChange([...addressList]);
    }

    if (isAddress && addFormEnabledByDefault) {
      isOneAddress && setIsEnabledAdd(false);
      !addressForm.length && addNewForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressForm]);

  useEffect(() => {
    editContactFormData && editContactForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContactFormData]);

  const editContactForm = () => {
    const updateData = {
      id: editContactFormData?.id,
      contactName: editContactFormData?.contactName,
      organization: editContactFormData?.organization,
      phone1: editContactFormData?.phone1,
      phone2: editContactFormData?.phone2,
      email: editContactFormData?.email,
      notes: editContactFormData?.notes,
      contactType: editContactFormData?.contactType,
      isActive: editContactFormData?.isActive,
    };

    setContactForm([
      ...contactForm,
      {
        prevValue: updateData,
        value: updateData,
      },
    ]);
  };

  useEffect(() => {
    editAddressFormData && editAddressForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAddressFormData]);

  const editAddressForm = () => {
    const updateData = {
      id: editAddressFormData?.id,
      placeName: editAddressFormData?.placeName,
      line1: editAddressFormData?.line1,
      line2: editAddressFormData?.line2,
      suburb: editAddressFormData?.suburb,
      googleAddress: editAddressFormData?.googleAddress,
      googleResponse: editAddressFormData?.googleResponse,
      city: editAddressFormData?.city,
      postCode: editAddressFormData?.postCode,
      addressType: editAddressFormData?.addressType,
      stateId: editAddressFormData?.stateId,
      countryId: editAddressFormData?.countryId,
      stateName: editAddressFormData?.stateName,
      countryName: editAddressFormData?.countryName,
      name: editAddressFormData?.name,
      contactNumber: editAddressFormData?.contactNumber,
      contactCompany: editAddressFormData?.contactCompany,
      isManual: editAddressFormData?.isManual,
      isActive: editAddressFormData?.isActive,
    };
    if (isOneAddress) {
      setAddressForm([
        {
          prevValue: updateData,
          value: updateData,
        },
      ]);
    } else {
      setAddressForm([
        ...addressForm,
        {
          prevValue: updateData,
          value: updateData,
        },
      ]);
    }
  };

  const addNewForm = () => {
    let data;
    if (isAddress) {
      data = {
        placeName: '',
        line1: '',
        line2: '',
        suburb: '',
        googleAddress: '',
        googleResponse: '',
        city: '',
        postCode: '',
        addressType: 'Main',
        stateName: '',
        countryName: DEFAULT_ADDRESS_COUNTRY,
        contactNumber: '',
        contactCompany: '',
        isManual: false,
        isActive: true,
      };
      setAddressForm([
        ...addressForm,
        {
          prevValue: null,
          value: data,
        },
      ]);

      isOneAddress && setIsEnabledAdd(false);
    } else {
      data = {
        contactName: '',
        organization: '',
        phone1: '',
        phone2: '',
        email: '',
        notes: '',
        contactType: contactType && contactType.length > 0 ? contactType[0].value : '',
        isActive: true,
      };
      setContactForm([
        ...contactForm,
        {
          prevValue: null,
          value: data,
        },
      ]);
    }
    isOneAddress && setIsEnabledAdd(false);
  };

  const handleAddressCancel = (index: number) => {
    if (addressForm[index].prevValue) {
      addressFormData[index] = addressForm[index].prevValue;
      onStructureAddressChange && onStructureAddressChange([...addressFormData]);
      handleAddressCreate && handleAddressCreate(index);
    }
    setIsEnabledAdd(true);
    addressFormData.splice(index, 1);
    addressForm.splice(index, 1);
    setAddressForm([...addressForm]);
  };

  const handleContactCancel = (index: number) => {
    if (contactForm[index].prevValue) {
      contactFormData[index] = contactForm[index].prevValue;
      onContactChange && onContactChange([...contactFormData]);
      handleContactCreate && handleContactCreate(index);
    }
    contactFormData.splice(index, 1);
    contactForm.splice(index, 1);
    setContactForm([...contactForm]);
  };

  const addressRequiredCheck = (index: number) => {
    let isValid;
    if (addressForm[index].value.isManual) {
      const filteredFields = addressRequiredFields.filter((field) =>
        isEmptyString(addressForm[index].value[field])
      );
      isValid = filteredFields.length === 0;
    } else {
      isValid = !isEmptyString(addressForm[index].value['googleAddress']);
    }
    return isValid;
  };

  const [addressTypeData] = useState(ADDRESS_CONTACT_TYPES);

  // `flush` drops the heading with the card chrome; `hideHeading` drops it on
  // its own, for a caller that keeps the chrome but titles the block itself.
  const showHeading = !!heading && !flush && !hideHeading;
  const canEdit = !!hasContractAddressEditPermission;
  // Carry each row's index in the *original* dataset alongside it. Delete is a
  // soft delete — it flips `isActive` and leaves the row in place — so once
  // anything has been deleted the visible list and the dataset no longer line
  // up, and every consumer indexes the dataset rather than what is on screen.
  // Handing over the filtered index made the next edit or delete hit the wrong
  // record.
  const addressRows =
    isOneAddress && isEditMode
      ? []
      : (addressDataset ?? [])
          .map((data, datasetIndex) => ({ data, datasetIndex }))
          .filter(({ data }) => data?.isActive);
  const contactRows = (contactDataset ?? [])
    .map((data, datasetIndex) => ({ data, datasetIndex }))
    .filter(({ data }) => data?.isActive);
  const rowCount = isAddress ? addressRows.length : contactRows.length;

  const formCount = isAddress ? addressForm.length : contactForm.length;

  return (
    <RoleBox flush={flush}>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: flush ? 0 : 1 }}>
        {showHeading && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32, mb: 0.5 }}>
            <StyledTypography variant={TypographyVariantProps.H6}>{heading}</StyledTypography>
          </Box>
        )}

        <Box>
          {isAddress
            ? addressRows.map(({ data, datasetIndex }, position) => (
                <AddressContactRow
                  key={datasetIndex}
                  type={hideAddressType ? undefined : data?.addressType}
                  detail={formatAddress(data)}
                  showDivider={position > 0}
                  canEdit={canEdit}
                  unmatchedTerm={unmatchedSuburb}
                  unmatchedReason={
                    unmatchedSuburb
                      ? `No zone matches “${unmatchedSuburb}” for this postcode`
                      : undefined
                  }
                  onEdit={(e) => {
                    onClickAddressEdit && onClickAddressEdit(e, datasetIndex);
                    isOneAddress && setIsEditMode(true);
                  }}
                  onDelete={
                    isOneAddress
                      ? undefined
                      : (e) => {
                          onClickAddressDelete && onClickAddressDelete(e, datasetIndex);
                          setIsEnabledAdd(true);
                        }
                  }
                />
              ))
            : contactRows.map(({ data, datasetIndex }, position) => (
                <AddressContactRow
                  key={datasetIndex}
                  type={data?.contactType}
                  detail={[data?.contactName, data?.organization, data?.phone1, data?.email]
                    .filter(Boolean)
                    .join(' · ')}
                  showDivider={position > 0}
                  canEdit={canEdit}
                  onEdit={(e) => onClickContactEdit && onClickContactEdit(e, datasetIndex)}
                  onDelete={(e) => onClickContactDelete && onClickContactDelete(e, datasetIndex)}
                />
              ))}
        </Box>
        {isOneAddress && !isEditMode
          ? null
          : isAddress &&
            addressForm?.map((data, index) => (
              <EditFormShell
                key={index}
                showDivider={rowCount > 0 || index > 0}
                canEdit={hasContractAddressEditPermission}
                onCancel={() => {
                  handleAddressCancel(index);
                  isOneAddress && setIsEditMode(false);
                }}
                onSave={() => {
                  if (addressRequiredCheck(index)) {
                    handleAddressCreate && handleAddressCreate(index);
                    const reducedArr = [...addressForm];
                    reducedArr.splice(index, 1);
                    setAddressForm([...reducedArr]);
                  }
                  isOneAddress && setIsEditMode(false);
                }}
              >
                <StructureAddress
                  switchLabel="Manual"
                  addressData={data.value}
                  onChange={(updatedData) => {
                    addressFormData[index] = updatedData;
                    addressForm[index].value = updatedData;
                    setAddressForm([...addressForm]);
                    onStructureAddressChange && onStructureAddressChange([...addressFormData]);
                  }}
                  addressTypeData={addressTypeData}
                  enableContactNumber={enableContactNumber}
                  enableAddressContactFields={enableAddressContactFields}
                  isOneAddress={isOneAddress}
                  isEditMode={isEditMode}
                  hasAddressEditPermission={hasContractAddressEditPermission}
                />
              </EditFormShell>
            ))}
        {isAddress ||
          contactForm?.map((data, index) => (
            <EditFormShell
              key={index}
              showDivider={rowCount > 0 || index > 0}
              canEdit={hasContractAddressEditPermission}
              onCancel={() => handleContactCancel(index)}
              onSave={() => {
                handleContactCreate && handleContactCreate(index);
                const reducedArr = [...contactForm];
                reducedArr.splice(index, 1);
                setContactForm([...reducedArr]);
              }}
            >
              <Contact
                contactData={data.value}
                contactType={contactType}
                onChange={(updatedData) => {
                  contactFormData[index] = updatedData;
                  contactForm[index].value = updatedData;
                  setContactForm([...contactForm]);
                  onContactChange && onContactChange([...contactFormData]);
                }}
                renderNotes={true}
                hasContactEditPermission={hasContractAddressEditPermission}
              />
            </EditFormShell>
          ))}

        {isEnabledAdd && canEdit && (
          <AddRowButton
            label={isAddress ? 'Add address' : 'Add contact'}
            onClick={() => addNewForm()}
            showDivider={rowCount > 0 || formCount > 0}
          />
        )}
      </Box>
    </RoleBox>
  );
};
